import { getApiBaseUrl } from "./apiBase";
const API_BASE_URL = getApiBaseUrl();

export const userService = {
  async getMe() {
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json")
        ? await res.json()
        : { msg: await res.text() };
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("role");
        return null;
      }
      if (!res.ok) throw new Error(data.msg || "Failed to fetch profile");
      return data; // Return full data to include token recovery if any
    } catch {
      return null;
    }
  },

  async updateMe(update) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/me`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(update),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to update profile");
    const merged = { ...data.user };
    if (update && typeof update === "object") {
      merged.customId = data.user.customId ?? merged.customId;
      merged.name = update.name ?? merged.name;
      merged.address = update.address ?? merged.address;
      merged.phone = update.phoneNumber ?? update.phone ?? merged.phone;
      merged.state = update.state ?? merged.state;
      merged.businessName = update.businessName ?? merged.businessName;
      merged.businessType = update.businessType ?? merged.businessType;
      merged.location = update.location ?? merged.location;
      merged.contactName = update.contactName ?? merged.contactName;
      merged.pickupAddress = update.pickupAddress ?? merged.pickupAddress;
      merged.senderState = update.senderState ?? merged.senderState;
      merged.pickupNote = update.pickupNote ?? merged.pickupNote;
      merged.centerAddress = update.centerAddress ?? merged.centerAddress;
    }
    localStorage.setItem("user", JSON.stringify(merged));
    return merged;
  },

  async getStats() {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to fetch user stats");
    return data; // { totalUsers, byRole }
  },

  async getAll() {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to fetch users");
    return data.users;
  },

  async createUser(payload) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to create user");
    return data.user;
  },

  async inviteUser(email, role, centerAddress) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email, role, centerAddress }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to invite user");
    return data;
  },

  async updateRole(id, role) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/${id}/role`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ role }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to update role");
    return data.user;
  },

  async updateMyRole(role) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/me/role`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ role }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to update role");
    // Persist user and role locally
    localStorage.setItem("user", JSON.stringify(data.user));
    localStorage.setItem("role", data.user?.role || role);
    return data.user;
  },

  async deleteUser(id) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "Failed to delete user");
    return data;
  },

  async searchUserById(customId) {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE_URL}/users/search/${customId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg || "User not found");
    return data.user;
  },
};
